/**
 * GameConfirmScreen.tsx
 * Game confirmation screen
 */

import { type FC } from 'react';

import { Button, TextBlock } from '@skillarea/lib-frontend';
import { Title } from '@skillarea/lib-frontend';
import { formatMoney } from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import styles from '../MatchmakingModal.module.scss';
import { formatCountdownMs } from '../utils/formatCountdownMs';

export interface IGameConfirmScreenProps {
  opponentName: string;
  stake: number;
  timeLeftMs: number;
  onAccept: () => void;
  onDecline: () => void;
}

export const GameConfirmScreen: FC<IGameConfirmScreenProps> = ({
  onAccept,
  onDecline,
  opponentName,
  stake,
  timeLeftMs,
}) => {
  const { t } = useTranslation('matchmaking');
  const countdown = formatCountdownMs(timeLeftMs);

  return (
    <div className={styles.screen} data-testid="game-confirm-screen">
      <div className={`${styles.centered} ${styles.resultScreen}`}>
        <Title className={styles.resultTitle} color="contrast" variant="benzin">
          {t('confirm.gameFound')}
        </Title>

        <Title color="accent" size="l">
          {formatMoney(stake)}
        </Title>

        <TextBlock className={styles.infoLabel} color="contrast" size="xl">
          {t('confirm.opponent', { name: opponentName })}
        </TextBlock>

        <TextBlock
          className={styles.searchTimer}
          color="warning"
          data-testid="matchmaking-confirm-game-timer"
        >
          {t('confirm.acceptWithin', { time: countdown })}
        </TextBlock>
      </div>

      <div className={styles.footer}>
        <div className={styles.actionButtons}>
          <Button
            className={styles.acceptButton}
            color="accent"
            data-testid="matchmaking-confirm-game-button"
            name={t('common.accept')}
            onClick={onAccept}
            size="s"
            variant="filled"
          >
            {t('common.accept')}
          </Button>
          <Button
            className={styles.declineButton}
            color="primary"
            data-testid="matchmaking-decline-game-button"
            name={t('common.decline')}
            onClick={onDecline}
            size="s"
            variant="filled"
          >
            {t('common.decline')}
          </Button>
        </div>
      </div>
    </div>
  );
};
